import { useMemo, useState } from 'react';
import { Select } from 'antd';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import clsx from 'clsx';
import { InfoIcon } from './fields';
import { FIELD_LABELS, FIELD_PLACEHOLDERS, paymentGroupPopover } from './constants';
import { usePaymentGroups } from '../../../../hooks/usePaymentGroups';
import type { ClientFormValues, PaymentGroup } from '../../../../types/ClientManagementTypes';
import styles from './ClientFormModal.module.scss';

const selectPopupContainer = () => document.body;
const selectCaret = <span className={styles.selectCaret} />;

// A payment group is billed to an external organization or the client itself;
// the field must accept a name that already exists or a new one to create.
const PaymentGroupField = () => {
  const { paymentGroups, isLoadingPaymentGroups, isPaymentGroupsLoadingError } = usePaymentGroups();
  const { control, setValue, clearErrors } = useFormContext<ClientFormValues>();
  const { field, fieldState } = useController<ClientFormValues>({ control, name: 'paymentGroupName' });
  const paymentGroupId = useWatch({ control, name: 'paymentGroupId' });
  const isNewPaymentGroup = !paymentGroupId && !!(field.value as string)?.trim();

  // searchValue is purely what the input displays; filterText is what narrows
  // the options -- kept separate so reopening with a committed value shows the
  // full list instead of filtering everything else out by that value.
  const [searchValue, setSearchValue] = useState('');
  const [filterText, setFilterText] = useState('');

  const trimmedFilter = filterText.trim();
  const exactMatch = useMemo(
    () => paymentGroups.find((group) => group.name.toLowerCase() === trimmedFilter.toLowerCase()),
    [paymentGroups, trimmedFilter],
  );

  const options = useMemo(() => {
    const existing = paymentGroups
      .filter((group) => !trimmedFilter || group.name.toLowerCase().includes(trimmedFilter.toLowerCase()))
      .map((group) => ({ value: group.name, label: group.name }));
    // Offered only while the typed text has no exact match, so an existing name can't be "created" twice.
    if (trimmedFilter && !exactMatch) existing.push({ value: trimmedFilter, label: `${trimmedFilter} (New)` });
    return existing;
  }, [exactMatch, paymentGroups, trimmedFilter]);

  const commitValue = (name: string) => {
    if (fieldState.error) clearErrors('paymentGroupName');
    const selected: PaymentGroup | undefined = paymentGroups.find((group) => group.name === name);
    setValue('paymentGroupId', selected?.id ?? null, { shouldDirty: true });
    field.onChange(name);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setFilterText(value);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) return;
    // The current value becomes the visible text, but the list starts unfiltered.
    setSearchValue((field.value as string) ?? '');
    setFilterText('');
  };

  const handleClear = () => {
    setValue('paymentGroupId', null, { shouldDirty: true });
    field.onChange('');
    setSearchValue('');
    setFilterText('');
  };

  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>
        {FIELD_LABELS.PAYMENT_GROUP}
        <InfoIcon content={paymentGroupPopover} />
      </label>
      <Select
        showSearch
        allowClear
        value={(field.value as string) || undefined}
        searchValue={searchValue}
        onSearch={handleSearch}
        onChange={commitValue}
        onClear={handleClear}
        onDropdownVisibleChange={handleOpenChange}
        onBlur={field.onBlur}
        options={options}
        placeholder={FIELD_PLACEHOLDERS.PAYMENT_GROUP}
        loading={isLoadingPaymentGroups}
        // The typed text is the value being created, so antd's own match would hide it.
        filterOption={false}
        notFoundContent={isPaymentGroupsLoadingError ? 'Unable to load payment groups.' : null}
        suffixIcon={selectCaret}
        getPopupContainer={selectPopupContainer}
        popupClassName={styles.selectPopup}
        className={clsx(styles.selectControl, styles.paymentGroupSelect, { [styles.hasError]: fieldState.error })}
      />
      <span className={clsx(styles.errorSlot, { [styles.hintSlot]: !fieldState.error && isNewPaymentGroup })}>
        {fieldState.error?.message ?? (isNewPaymentGroup ? 'New group — will be created on save.' : null)}
      </span>
    </div>
  );
};

export default PaymentGroupField;
